import _ from 'lodash';
import { ContentMaps } from '../../api/models/BibleStudy';
import { CreateBibleStudySection } from '../../api/models/BibleStudySection';
import { BookAltSvg } from '../../assets/svg-icon-components/BookAltSvg';
import { EditSvg } from '../../assets/svg-icon-components/EditSvg';
import { FileMusicSvg } from '../../assets/svg-icon-components/FileMusicSvg';
import { FileSvg } from '../../assets/svg-icon-components/FileSvg';
import { PictureSvg } from '../../assets/svg-icon-components/PictureSvg';
import { PlayAltSvg } from '../../assets/svg-icon-components/PlayAltSvg';
import { TextSvg } from '../../assets/svg-icon-components/TextSvg';
import { ModuleType, SectionModule } from './../../api/models/BibleStudyModule';

/**
 * It generates a unique key for each file input.
 */
export const generateFileInputKeys = () => ({
  image: 'img' + new Date(),
  video: 'vid' + new Date(),
  document: 'doc' + new Date(),
  scripture: 'script' + new Date(),
  audio: 'audio' + new Date(),
});

/**
 * It takes an object with a name and tag property and returns an object with a tag, name, and modules property
 * @param {Args}  - Args - the parameters that are passed into the function
 */
export const initializeSection = ({ name, tag }: { name?: string; tag?: string }): CreateBibleStudySection => ({
  tag: tag || '',
  name: name || '',
  modules: [],
});

/**
 * It takes an array of sections and returns an array of unique tags
 * @param {CreateBibleStudySection[]} sections - CreateBibleStudySection[]
 * @returns An array of unique strings.
 */
export const getSectionTags = (sections: CreateBibleStudySection[]): string[] => {
  const names = sections.map(section => section.name);
  const tags = sections.map(section => section.tag || '');

  return Array.from(new Set([...names, ...tags])).filter(tag => tag);
};

/**
 * "Get all the tags from all the modules in all the sections."
 *
 * The function takes an array of sections, and returns an array of tags
 * @param {CreateBibleStudySection[]} sections - CreateBibleStudySection[]
 * @returns An array of unique tags from the modules in the sections.
 */
export const getModuleTags = (sections: CreateBibleStudySection[]): string[] => {
  const tags = sections.flatMap(section => section.modules.map(module => module.tag || ''));

  return Array.from(new Set(tags)).filter(tag => tag);
};

/**
 * It takes a section and returns an array of unique tags from all the modules in that section
 * @param {CreateBibleStudySection} section - CreateBibleStudySection
 * @returns An array of unique tags from the modules in the section.
 */
export const getModuleTagsBySection = (section: CreateBibleStudySection): string[] => {
  const tags = section.modules.map(module => module.tag || '');

  return Array.from(new Set(tags)).filter(tag => tag);
};

/**
 * It takes in a weekTag and a dayTag and returns a string that is the concatenation of the two
 * @param {string} weekTag - The week tag of the module group.
 * @param {string} dayTag - The day of the week, e.g. "Day 1"
 * @returns A string that is the concatenation of the weekTag and dayTag.
 */
export const getModuleGroupKey = (weekTag: string, dayTag: string) => {
  return `${weekTag}_${dayTag}`;
};

/**
 * It takes an array of sections and returns an object with the section tag and module tag as the key and an array of modules
 * as the value
 * @param {CreateBibleStudySection[]} sections - CreateBibleStudySection[]
 * @returns A library of modules grouped by section and module tag.
 */
export const getModuleGroupsLibrary = (sections: CreateBibleStudySection[]) => {
  // Generated library with "{weekTag}_{dayTag}" format for keys
  const library: { [key: string]: SectionModule[] } = {};

  sections.forEach(section => {
    section.modules.forEach(module => {
      const key = `${section.tag}_${module.tag}`;
      if (!Array.isArray(library[key])) {
        library[key] = [];
      }
      library[key].push(module);
    });
  });

  return library;
};

/**
 * It takes in an array of sections and returns an object that maps section tags to module tags to an array of module indexes
 * @example returns
 * ```
 * {
 *  "Week 2": {
 *    "Day 1": [0,1],
 *    "Day 2": [2],
 *    "Day 3": [3]
 *  },
 *  "Week 3": {
 *    "Day 1": [0,1,3],
 *    "Day 2": [2]
 *  }
 * }
 * ```
 * @param {CreateBibleStudySection[]} sections - CreateBibleStudySection[]
 * @returns A library of the index of the modules in the sections.
 */
export const getModuleIndexLibrary = (sections: CreateBibleStudySection[]) => {
  const library: { [weekTag: string]: { [dayTag: string]: number[] } } = {};

  sections.forEach(section => {
    const sectionTag = section.tag || section.name;

    const isSectionInLib = !_.isNil(library[sectionTag]);
    if (!isSectionInLib) {
      library[sectionTag] = {};
    }

    const moduleTags = getModuleTagsBySection(section);
    moduleTags.forEach(tag => {
      const isTagInLib = !_.isNil(library[sectionTag][tag]);
      if (!isTagInLib) {
        library[sectionTag][tag] = [];
      }

      section.modules.forEach((module, i) => {
        if (module.tag?.toLowerCase() === tag.toLowerCase()) {
          library[sectionTag][tag].push(i);
        }
      });
    });
  });

  return library;
};

/**
 * Modules in the back-end looks like this (A):
 * ```text
 * - section:
 *  - module: { tag: 'day 1', content: string } // index 0
 *  - module: { tag: 'day 1', content: string } // index 1
 *  - module: { tag: 'day 2', content: string } // index 2
 * - section
 *  - module: { tag: 'day 1', content: string } // index 0
 *  - module: { tag: 'day 2', content: string } // index 1
 *  - module: { tag: 'day 2', content: string } // index 2
 * ```
 *
 * However, it's being presented like this (B):
 * ```text
 * - section
 *  - day 1 (via tag) // index 0
 *    - module: { tag: 'day 1', content: string } // index 0
 *    - module: { tag: 'day 1', content: string } // index 1
 *  - day 2 (via tag) // index: 1
 *    - module: { tag: 'day 2', content: string } // index 0
 * - section
 *  - day 1 (via tag) // index 0
 *    - module: { tag: 'day 1', content: string } // index 0
 *  - day 2 (via tag) // index 1
 *    - module: { tag: 'day 2', content: string } // index 0
 *    - module: { tag: 'day 2', content: string } // index 1
 * ```
 *
 * This method gets the module position in (A) given position in (B)
 */
export const getModuleArrayIndex = (
  sections: CreateBibleStudySection[],
  args: { weekTag: string; dayTag: string; moduleDayIndex: number }
): number => {
  const { weekTag, dayTag, moduleDayIndex } = args;
  const lib = getModuleIndexLibrary(sections);

  return lib[weekTag][dayTag][moduleDayIndex];
};

/**
 * Get list of day options given a week
 * @param {ContentMaps} contentMaps
 * @param {string} currentWeek - e.g. "Week 1"
 */
export const getDayOptions = (contentMaps: ContentMaps, currentWeek: string) => {
  const week: number = parseInt(currentWeek.split(' ')[1]);
  if (!week) return [];

  const options = [];
  for (let i = 1; i <= contentMaps[week - 1].days; i++) {
    options.push({ label: `Day ${i}`, value: `Day ${i}` });
  }

  return options;
};

export const getModuleIcon = (type: ModuleType) => {
  const icons = {
    scripture: BookAltSvg,
    qanda: EditSvg,
    document: FileSvg,
    audio: FileMusicSvg,
    image: PictureSvg,
    video: PlayAltSvg,
    text: TextSvg,
  };

  return icons[type];
};
